import React from 'react';
import CustomNavbar from './common/Navbar';
import Footer from './common/Footer';
import ScrollToTop from './common/ScrollToTop';
import '../assets/styles/terms.css';
import '../assets/styles/scrollToTop.css';

const TermsAndConditions = () => {
  return (
    <div className="terms-page">
      <CustomNavbar />
      <div className="terms-container">
        <div className="container">
          <div className="terms-content">
            <h1>Terms of Service</h1>
            <p className="last-updated"><strong>Last Updated: October 16, 2025</strong></p>

            <section className="terms-section">
              <h2>1. Agreement to Terms</h2>
              <p>
                By accessing or using PacDocSign ("Service," "we," "us," or "our") operated by Pacific Document Signing Inc., 
                located at 2154 Westwood Blvd. Suite #300, Los Angeles, CA 90025, you agree to be bound by these Terms of Service ("Terms"). 
                If you disagree with any part of these terms, you may not access the Service.
              </p>
            </section>

            <section className="terms-section">
              <h2>2. Description of Service</h2>
              <p>PacDocSign is a professional notary signing service platform that connects signing agents with document signing assignments. Our Service includes:</p>
              <ul>
                <li><strong>Order Management System</strong>: Assignment creation, tracking, and completion</li>
                <li><strong>Document Management</strong>: Secure upload, storage, delivery, and tracking with customer-supplied encryption</li>
                <li><strong>Communication Services</strong>: 
                  <ul>
                    <li>SMS notifications for appointment reminders and confirmations</li>
                    <li>Email notifications for assignments and updates</li>
                  </ul>
                </li>
                <li><strong>Geographic Matching</strong>: Location-based assignment matching using GPS coordinates</li>
                <li><strong>Scheduling System</strong>: Appointment coordination and calendar management</li>
                <li><strong>Payment Processing</strong>: Invoicing, billing, and payment tracking with QuickBooks integration</li>
                <li><strong>Performance Tracking</strong>: Assignment completion rates and quality metrics</li>
                <li><strong>Reporting Tools</strong>: Financial reports, activity logs, and analytics</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>3. User Accounts and Registration</h2>
              
              <h3>3.1 Account Types</h3>
              <p>We offer different account types with specific roles and permissions:</p>
              
              <h4>Signing Agents/Notaries</h4>
              <ul>
                <li><strong>Individual Accounts</strong>: Independent notary signing agents</li>
                <li><strong>Company Accounts</strong>: Signing companies with multiple sub-signers/employees
                  <ul>
                    <li>Parent account manages company profile and settings</li>
                    <li>Sub-signer accounts for individual agents under the company</li>
                  </ul>
                </li>
              </ul>

              <h4>Client Organizations</h4>
              <ul>
                <li><strong>Corporate Clients</strong>: Companies that request signing services</li>
                <li><strong>Division Management</strong>: Multi-division organizations with separate billing</li>
              </ul>

              <h4>Administrative Users</h4>
              <ul>
                <li><strong>Accountants</strong>: Financial management and reporting access</li>
                <li><strong>Managers</strong>: Assignment oversight and signer management</li>
                <li><strong>Employers/Administrators</strong>: Full system access and user management</li>
              </ul>

              <h3>3.2 Account Requirements</h3>
              <p>To create an account, you must:</p>
              <ul>
                <li>Be at least 18 years of age</li>
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>For Signing Agents: Possess valid notary credentials in your operating state(s)</li>
              </ul>

              <h3>3.3 Account Security</h3>
              <p>You are responsible for:</p>
              <ul>
                <li>Maintaining the confidentiality of your password and login credentials</li>
                <li>Restricting access to your computer and account</li>
                <li>All activities that occur under your account or sub-accounts (for company accounts)</li>
                <li>Immediately notifying us of any unauthorized use or security breach</li>
              </ul>
              <p>We implement security measures including:</p>
              <ul>
                <li>Secure password requirements</li>
                <li>Session timeout after periods of inactivity</li>
                <li>Optional two-factor authentication via email link</li>
                <li>Encrypted data transmission (SSL/TLS)</li>
                <li>Customer-supplied encryption keys (CSEK) for document storage</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>4. User Responsibilities</h2>
              
              <h3>4.1 For Signing Agents</h3>
              <p>Signing agents agree to:</p>
              <ul>
                <li>Possess and maintain valid notary commission and credentials</li>
                <li>Maintain current certifications (NNA, NSA, or other recognized bodies)</li>
                <li>Maintain professional liability insurance (E&O) and notary bonds as required</li>
                <li>Sign and strictly comply with PacDocSign's <strong>Confidentiality, Non-Disclosure, and Security Agreement</strong> (see Section 6.2)</li>
                <li>Protect the confidentiality of all borrower and client non-public personal information in accordance with GLBA requirements</li>
                <li>Use Confidential Information only for fulfilling assignment obligations to PacDocSign</li>
                <li>Perform all assignments professionally, accurately, and punctually</li>
                <li>Report any conflicts of interest or inability to complete assignments</li>
                <li>Follow all technical and physical security safeguards as specified in the GLBA Confidentiality Agreement</li>
                <li>Properly handle, secure, and dispose of sensitive documents per GLBA requirements</li>
                <li>Respond promptly to SMS and email communications regarding assignments</li>
                <li>Maintain accurate location/GPS information for assignment matching</li>
                <li>Keep emergency contact information current</li>
                <li>Submit accurate and timely invoices for completed work</li>
              </ul>

              <h3>4.2 For Company Account Holders</h3>
              <p>Companies with sub-signer accounts agree to:</p>
              <ul>
                <li>Verify credentials and qualifications of all sub-signers</li>
                <li>Ensure all sub-signers comply with these Terms</li>
                <li>Maintain responsibility for all sub-signer activities</li>
                <li>Keep accurate records of sub-signer assignments</li>
                <li>Manage payment distribution to sub-signers</li>
              </ul>

              <h3>4.3 For Client Organizations</h3>
              <p>Clients agree to:</p>
              <ul>
                <li>Provide accurate and complete assignment information</li>
                <li>Submit necessary documents in a timely manner</li>
                <li>Pay for services according to agreed-upon terms and schedules</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Respect signing agents' professional independence</li>
                <li>Provide accurate borrower contact information</li>
                <li>Confirm or cancel appointments according to established timelines</li>
              </ul>

              <h3>4.4 Prohibited Activities</h3>
              <p>All users may not:</p>
              <ul>
                <li>Violate any applicable federal, state, or local laws or regulations</li>
                <li>Infringe on intellectual property rights of PacDocSign or others</li>
                <li>Transmit harmful code, viruses, malware, or any disruptive technology</li>
                <li>Interfere with the proper functioning of the Service</li>
                <li>Attempt unauthorized access to any part of the Service, servers, or databases</li>
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Share confidential borrower/customer information inappropriately or outside the scope of assignments</li>
                <li>Engage in fraudulent activities or misrepresentation</li>
                <li>Use automated systems (bots, scrapers) without written permission</li>
                <li>Circumvent security features or access controls</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>5. SMS and Communication Services</h2>
              
              <h3>5.1 SMS Notifications</h3>
              <p>By opting in to SMS notifications, you consent to receive:</p>
              <ul>
                <li>Appointment confirmation requests (Reply 'Yes' to confirm availability)</li>
                <li>Appointment reminders with borrower and timing details</li>
                <li>Assignment updates and changes</li>
                <li>System notifications</li>
              </ul>
              <p>You acknowledge and agree that:</p>
              <ul>
                <li>SMS is sent via third-party providers (currently migrating from Plivo to Twilio)</li>
                <li>Standard message and data rates may apply based on your carrier</li>
                <li>Message frequency varies based on your appointment volume</li>
                <li>You can opt-out at any time by replying "REMOVE" or "STOP"</li>
                <li>Opting out may affect your ability to receive time-sensitive assignment information</li>
                <li>You must maintain accurate mobile phone information for SMS delivery</li>
              </ul>

              <h3>5.2 SMS Keywords</h3>
              <p>Recognized SMS keywords and their functions:</p>
              <ul>
                <li><strong>"Yes" or "Y"</strong>: Confirm appointment availability</li>
                <li><strong>"No" + reason</strong>: Decline appointment or report unavailability</li>
                <li><strong>"REMOVE" or "STOP"</strong>: Unsubscribe from SMS notifications</li>
              </ul>

              <h3>5.3 Email Communications</h3>
              <p>By using the Service, you consent to receive:</p>
              <ul>
                <li>Assignment notifications and confirmations</li>
                <li>Document delivery notifications</li>
                <li>Invoice and payment communications</li>
                <li>System updates and announcements</li>
                <li>Account and security notifications</li>
              </ul>
              <p>Email is sent via:</p>
              <ul>
                <li>SendGrid for automated notifications</li>
                <li>Office365 SMTP for operational communications</li>
              </ul>

              <h3>5.4 Communication Standards</h3>
              <ul>
                <li>All communications must be professional and business-related</li>
                <li>Users must respond promptly to time-sensitive messages</li>
                <li>Communication channels must not be used for personal matters unrelated to assignments</li>
                <li>Abusive, threatening, or inappropriate communication may result in account suspension</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>6. Privacy and Data Protection</h2>
              
              <h3>6.1 Data Collection and Use</h3>
              <p>We collect and process personal information as described in our Privacy Policy, including:</p>
              <ul>
                <li><strong>Account Information</strong>: Name, email, phone numbers, address</li>
                <li><strong>Professional Credentials</strong>: Notary commission, certifications, insurance details</li>
                <li><strong>Assignment Data</strong>: Order details, completion records, performance metrics</li>
                <li><strong>Communication Records</strong>: SMS logs, email history, system messages</li>
                <li><strong>Location Data</strong>: GPS coordinates for geographic assignment matching</li>
                <li><strong>Payment Information</strong>: Banking details, invoices, payment history</li>
                <li><strong>Emergency Contacts</strong>: For signing agent safety and communication</li>
              </ul>

              <h3>6.2 GLBA Compliance</h3>
              <p><strong>For Signing Agents:</strong> The Gramm-Leach-Bliley Act (GLB Act, 15 USC 6801, et seq.) imposes obligations on financial institutions and their service providers regarding the confidentiality and security of customer data. As signing agents perform services for financial institutions through PacDocSign, they must:</p>
              <ul>
                <li>Sign PacDocSign's <strong>Confidentiality, Non-Disclosure, and Security Agreement</strong> before receiving any assignments</li>
                <li>Use Confidential Information (including all non-public personal information as defined under the GLB Act) <strong>only</strong> for fulfilling assignment obligations</li>
                <li>Not disclose, publish, release, transfer, or make available Confidential Information in any form, except as necessary to fulfill assignment obligations</li>
                <li>Not make copies of Confidential Information except for the signatory/borrower</li>
                <li>Implement specific security measures including:
                  <ul>
                    <li>Use password-protected computers for accessing loan files</li>
                    <li>Delete downloaded files after printing</li>
                    <li>Install and maintain ISCA-certified anti-virus software (e.g., Norton, McAfee, ESET) with current updates</li>
                    <li>Use only web browsers with SSL/TLS 1.3 or higher protection and 256-bit or greater encryption (Chrome 30+, Firefox 27+, Microsoft Edge, IE 11+, Safari 9+)</li>
                    <li>Shred all extra hardcopy loan documents</li>
                    <li>Store hardcopy documents out of plain sight in secure locations</li>
                    <li>Keep documents in sealed, opaque wrappers during transport; if left in vehicle, place in trunk before arriving at destination</li>
                    <li>Use lender-provided shipping labels for document return</li>
                  </ul>
                </li>
                <li>Report any security incidents immediately, including:
                  <ul>
                    <li>Lost or missing physical documents</li>
                    <li>Lost or missing devices containing electronic documents</li>
                    <li>Security breaches of computers used to access loan documents</li>
                    <li>File police reports for stolen data</li>
                    <li>Grant permission for investigation, including possible physical inspection of computers</li>
                  </ul>
                </li>
              </ul>
              <p><strong>Important:</strong> If the GLB Act or other applicable law imposes a higher standard of confidentiality than these Terms, such higher standard shall take precedence.</p>
              <p>Unauthorized disclosure or use of Confidential Information will cause immediate and irreparable injury, and PacDocSign may seek injunctive relief in addition to other remedies.</p>

              <h3>6.3 Data Security</h3>
              <p>We implement industry-standard security measures:</p>
              <ul>
                <li><strong>Encryption</strong>: SSL/TLS for all data transmission</li>
                <li><strong>Document Storage</strong>: Customer-Supplied Encryption Keys (CSEK) via Google Cloud Storage</li>
                <li><strong>Authentication</strong>: Secure password requirements with optional two-factor authentication</li>
                <li><strong>Access Controls</strong>: Role-based permissions and activity monitoring</li>
                <li><strong>Session Management</strong>: Automatic timeouts and secure session tokens</li>
                <li><strong>Regular Audits</strong>: Security reviews and vulnerability assessments</li>
              </ul>

              <h3>6.4 Data Retention</h3>
              <ul>
                <li>Account information retained while account is active</li>
                <li>Assignment records retained per legal and regulatory requirements</li>
                <li>Document audit trails maintained for compliance purposes</li>
                <li>Deleted accounts may have certain data retained as required by law</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>7. Payment Terms</h2>
              
              <h3>7.1 Service Fees</h3>
              <ul>
                <li>Client organizations pay for signing services according to agreed-upon rates</li>
                <li>Signing agents are compensated based on assignment terms and fee schedules</li>
                <li>All fees and rates are subject to change with advance notice</li>
                <li>Additional fees may apply for:
                  <ul>
                    <li>Rush assignments</li>
                    <li>Extended travel distances</li>
                    <li>Specialized services (witnesses, scan-backs, etc.)</li>
                    <li>After-hours or weekend appointments</li>
                  </ul>
                </li>
              </ul>

              <h3>7.2 Billing and Invoicing</h3>
              <ul>
                <li><strong>Invoice Generation</strong>: Automated invoices generated upon assignment completion</li>
                <li><strong>Payment Terms</strong>: Net 30 days unless otherwise specified in client agreements</li>
                <li><strong>Billing Cycles</strong>: Weekly, bi-weekly, or monthly based on client preference</li>
                <li><strong>QuickBooks Integration</strong>: Invoices and bills synced with accounting system</li>
                <li><strong>Late Payments</strong>: May incur interest charges and late fees as permitted by law</li>
                <li><strong>Disputes</strong>: Must be raised within 30 days of invoice date</li>
                <li><strong>Payment Methods</strong>: Check, ACH, wire transfer, or other agreed methods</li>
              </ul>

              <h3>7.3 Signer Payments</h3>
              <ul>
                <li><strong>Payment Processing</strong>: Via check or direct deposit based on signer preference</li>
                <li><strong>Payment Schedule</strong>: According to established payment cycles</li>
                <li><strong>Invoice Submission</strong>: Signers must submit invoices within specified timeframes</li>
                <li><strong>Payment Tracking</strong>: Reference numbers provided for all payments</li>
                <li><strong>Tax Responsibility</strong>: Independent contractors responsible for tax reporting</li>
              </ul>

              <h3>7.4 Refunds and Adjustments</h3>
              <ul>
                <li>Refund requests must be submitted in writing within 30 days</li>
                <li>Service credits may be issued for valid service quality issues</li>
                <li>Fee adjustments require approval from authorized personnel</li>
                <li>Disputed charges will be investigated and resolved fairly</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>8. Intellectual Property</h2>
              
              <h3>8.1 Service Content and Ownership</h3>
              <p>All content, features, and functionality of the Service are owned by Pacific Document Signing, Inc. and protected by copyright, trademark, patent, and other intellectual property laws:</p>
              <ul>
                <li>Software code, algorithms, and technology infrastructure</li>
                <li>User interfaces, designs, and visual elements</li>
                <li>Text content, graphics, logos, and branding</li>
                <li>Trademarks, service marks, and trade names</li>
                <li>Databases, data structures, and system architecture</li>
                <li>Documentation, training materials, and guides</li>
              </ul>

              <h3>8.2 User Content License</h3>
              <ul>
                <li><strong>Ownership</strong>: You retain ownership of all content you upload to the Service</li>
                <li><strong>License Grant</strong>: You grant us a limited, non-exclusive, worldwide license to:
                  <ul>
                    <li>Store, process, and transmit your content as necessary for Service operation</li>
                    <li>Display content to authorized users (clients, assigned signers, administrators)</li>
                    <li>Create backups and ensure data redundancy</li>
                    <li>Generate reports and analytics (in aggregated, non-identifiable form)</li>
                  </ul>
                </li>
                <li><strong>Representations</strong>: You represent and warrant that:
                  <ul>
                    <li>You have all necessary rights to the content you provide</li>
                    <li>Your content does not violate any third-party rights</li>
                    <li>Your content complies with these Terms and applicable laws</li>
                  </ul>
                </li>
              </ul>

              <h3>8.3 Content Removal</h3>
              <p>We reserve the right to remove or disable access to content that:</p>
              <ul>
                <li>Violates these Terms or our policies</li>
                <li>Infringes on intellectual property rights</li>
                <li>Contains illegal or harmful material</li>
                <li>Poses security or privacy risks</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>9. Disclaimer of Warranties</h2>
              <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:</p>
              <ul>
                <li><strong>Merchantability</strong>: No warranty that the Service is suitable for commercial purposes</li>
                <li><strong>Fitness for Purpose</strong>: No guarantee the Service meets your specific requirements</li>
                <li><strong>Non-Infringement</strong>: No warranty against third-party intellectual property claims</li>
                <li><strong>Availability</strong>: No guarantee of uninterrupted, timely, secure, or error-free operation</li>
                <li><strong>Accuracy</strong>: No warranty regarding accuracy, completeness, or reliability of:
                  <ul>
                    <li>Information provided through the Service</li>
                    <li>Geographic matching algorithms</li>
                    <li>Assignment details or borrower information</li>
                    <li>Reports, analytics, or calculations</li>
                  </ul>
                </li>
                <li><strong>Third-Party Services</strong>: No responsibility for failures of SMS providers, email services, payment processors, or other integrated services</li>
                <li><strong>Data Loss</strong>: No guarantee against data loss, corruption, or unauthorized access</li>
              </ul>
              <p>Some jurisdictions do not allow the exclusion of implied warranties, so some of the above exclusions may not apply to you.</p>
            </section>

            <section className="terms-section">
              <h2>10. Limitation of Liability</h2>
              <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, PACIFIC DOCUMENT SIGNING, INC., ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR:</p>
              
              <h3>10.1 Types of Damages Excluded</h3>
              <ul>
                <li>Indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, business opportunities, or expected savings</li>
                <li>Loss of data, goodwill, or reputation</li>
                <li>Business interruption or lost productivity</li>
                <li>Costs of procurement of substitute services</li>
                <li>Any damages arising from:
                  <ul>
                    <li>Use or inability to use the Service</li>
                    <li>Unauthorized access to your account or data</li>
                    <li>Errors, bugs, or system failures</li>
                    <li>Third-party actions or content</li>
                    <li>Assignment cancellations or changes</li>
                    <li>SMS or email delivery failures</li>
                    <li>Payment processing delays</li>
                  </ul>
                </li>
              </ul>

              <h3>10.2 Cap on Liability</h3>
              <p>Our total aggregate liability for all claims related to the Service shall not exceed the greater of:</p>
              <ul>
                <li>The amount paid by you to us in the 12 months preceding the claim, or</li>
                <li>$500 USD</li>
              </ul>
              <p>This limitation applies regardless of the legal theory (contract, tort, negligence, strict liability, or otherwise).</p>

              <h3>10.3 State-Specific Exceptions</h3>
              <p>Some jurisdictions do not allow certain limitations on liability, so these limitations may not fully apply to you. You may have additional rights under your local laws.</p>
            </section>

            <section className="terms-section">
              <h2>11. Indemnification</h2>
              <p>You agree to indemnify, defend, and hold harmless Pacific Document Signing, Inc., its officers, directors, employees, contractors, agents, licensors, and suppliers from and against any and all claims, liabilities, damages, losses, costs, expenses, and fees (including reasonable attorneys' fees) arising from:</p>
              <ul>
                <li>Your use or misuse of the Service</li>
                <li>Your violation of these Terms or any applicable laws or regulations</li>
                <li>Your violation of any rights of another person or entity</li>
                <li>Your negligent or wrongful conduct in performing signing assignments</li>
                <li>Any content you upload, submit, or transmit through the Service</li>
                <li><strong>Breach of the Confidentiality, Non-Disclosure, and Security Agreement or failure to comply with GLBA requirements</strong> (for signing agents)</li>
                <li>Unauthorized disclosure or use of Confidential Information or non-public personal information</li>
                <li>Any false or misleading information you provide</li>
                <li>Actions of your sub-signers (for company accounts)</li>
                <li>Disputes with other users, clients, or signing agents</li>
                <li>Security incidents or data breaches resulting from your failure to implement required safeguards</li>
              </ul>
              <p>We reserve the right to assume exclusive defense and control of any matter subject to indemnification, and you agree to cooperate with our defense of such claims.</p>
            </section>

            <section className="terms-section">
              <h2>12. Service Modifications and Termination</h2>
              
              <h3>12.1 Service Changes</h3>
              <p>We reserve the right to, at our sole discretion:</p>
              <ul>
                <li>Modify, suspend, or discontinue any aspect of the Service at any time</li>
                <li>Update these Terms with reasonable advance notice (minimum 30 days for material changes)</li>
                <li>Add, remove, or modify features and functionality</li>
                <li>Change pricing, fee structures, or payment terms with 60 days advance notice</li>
                <li>Implement new security measures or authentication requirements</li>
                <li>Update third-party service providers (SMS, email, payment processing)</li>
              </ul>
              <p>Continued use of the Service after changes constitutes acceptance of modified Terms.</p>

              <h3>12.2 Account Suspension</h3>
              <p>We may immediately suspend your account without notice if:</p>
              <ul>
                <li>You violate these Terms or our policies</li>
                <li>You engage in fraudulent, illegal, or harmful activities</li>
                <li>Your account poses a security risk to the Service or other users</li>
                <li>We receive legal or regulatory orders requiring suspension</li>
                <li>You fail to respond to critical security or compliance requests</li>
              </ul>

              <h3>12.3 Account Termination</h3>
              <p>We may terminate your account if:</p>
              <ul>
                <li>You repeatedly violate these Terms</li>
                <li>You fail to maintain required credentials (for signing agents)</li>
                <li>Your account remains inactive for more than 12 consecutive months</li>
                <li>You provide false or misleading information</li>
                <li>You fail to pay outstanding fees or invoices (after reasonable notice)</li>
                <li>Termination is required by law or regulatory authority</li>
              </ul>
              <p>You may terminate your account at any time by:</p>
              <ul>
                <li>Contacting our support team at support@pacdocsign.com</li>
                <li>Providing written notice with 30 days advance notice for scheduled assignments</li>
                <li>Ensuring all outstanding obligations are fulfilled</li>
              </ul>

              <h3>12.4 Effect of Termination</h3>
              <p>Upon account termination:</p>
              <ul>
                <li>Your access to the Service will immediately cease</li>
                <li>You must cease all use of our intellectual property and branding</li>
                <li>All outstanding payment obligations remain in effect and become immediately due</li>
                <li>Pending assignments may be reassigned to other signing agents</li>
                <li>We may retain certain information as required by law or for legitimate business purposes</li>
                <li>The following provisions survive termination:
                  <ul>
                    <li>Payment obligations</li>
                    <li>Indemnification commitments</li>
                    <li>Intellectual property rights</li>
                    <li>Limitation of liability</li>
                    <li>Dispute resolution procedures</li>
                    <li>Confidentiality obligations</li>
                  </ul>
                </li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>13. Dispute Resolution</h2>
              
              <h3>13.1 Governing Law</h3>
              <p>These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law principles. The United Nations Convention on Contracts for the International Sale of Goods shall not apply.</p>

              <h3>13.2 Informal Dispute Resolution</h3>
              <p>Before filing any formal legal action, you agree to first contact us to attempt to resolve the dispute informally by sending written notice to:</p>
              <div className="contact-info">
                <p><strong>Pacific Document Signing Inc.</strong></p>
                <p>Attn: Legal Department</p>
                <p>2154 Westwood Blvd. Suite #300</p>
                <p>Los Angeles, CA 90025</p>
                <p>Email: legal@pacdocsign.com</p>
              </div>
              <p>We will attempt to resolve the dispute through good-faith negotiation within 60 days of receiving your notice.</p>

              <h3>13.3 Binding Arbitration</h3>
              <p>If informal resolution fails, any dispute, claim, or controversy arising from or relating to these Terms or the Service shall be resolved through final and binding arbitration administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules.</p>
              <p><strong>Arbitration Terms:</strong></p>
              <ul>
                <li><strong>Location</strong>: Los Angeles County, California</li>
                <li><strong>Arbitrator</strong>: Single arbitrator selected per AAA rules</li>
                <li><strong>Language</strong>: English</li>
                <li><strong>Costs</strong>: Each party bears their own costs unless otherwise awarded by the arbitrator</li>
                <li><strong>Discovery</strong>: Limited discovery as determined by the arbitrator</li>
                <li><strong>Judgment</strong>: Arbitration award may be entered as a judgment in any court of competent jurisdiction</li>
              </ul>
              <p><strong>Exceptions to Arbitration:</strong></p>
              <p>The following disputes may be brought in court:</p>
              <ul>
                <li>Small claims court matters (under applicable jurisdictional limits)</li>
                <li>Injunctive relief for intellectual property infringement</li>
                <li>Emergency relief to prevent irreparable harm</li>
              </ul>

              <h3>13.4 Class Action Waiver</h3>
              <p><strong>YOU AGREE THAT DISPUTES WILL BE RESOLVED ON AN INDIVIDUAL BASIS ONLY.</strong> You waive any right to:</p>
              <ul>
                <li>Participate in class action lawsuits</li>
                <li>Participate in class arbitrations</li>
                <li>Consolidate claims with other users</li>
                <li>Act as a class representative or member</li>
              </ul>
              <p>This waiver does not apply where prohibited by law.</p>

              <h3>13.5 Venue and Jurisdiction</h3>
              <p>For any disputes not subject to arbitration, you agree to submit to the personal and exclusive jurisdiction of the state and federal courts located in Los Angeles County, California.</p>
            </section>

            <section className="terms-section">
              <h2>14. Compliance and Regulatory Requirements</h2>
              
              <h3>14.1 Industry Regulations</h3>
              <p>Users must comply with all applicable regulations, including:</p>
              <ul>
                <li>State notary laws and regulations</li>
                <li>GLBA (Gramm-Leach-Bliley Act) for financial data protection</li>
                <li>FCRA (Fair Credit Reporting Act) for consumer information</li>
                <li>State privacy laws (CCPA, CPRA, etc.)</li>
                <li>Telephone Consumer Protection Act (TCPA) for SMS communications</li>
                <li>CAN-SPAM Act for email communications</li>
              </ul>

              <h3>14.2 Credential Verification</h3>
              <p>Signing agents must:</p>
              <ul>
                <li>Maintain valid state notary commission(s)</li>
                <li>Keep E&O insurance current and at required levels</li>
                <li>Renew credentials before expiration</li>
                <li>Notify us immediately of any credential suspensions or revocations</li>
                <li>Provide proof of credentials upon request</li>
              </ul>

              <h3>14.3 Audit Rights</h3>
              <p>We reserve the right to:</p>
              <ul>
                <li>Audit user compliance with these Terms</li>
                <li>Request documentation verifying credentials or qualifications</li>
                <li>Review assignment performance and quality metrics</li>
                <li>Investigate complaints or reported violations</li>
                <li>Share audit findings with clients or regulatory authorities as required</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>15. Miscellaneous Provisions</h2>
              
              <h3>15.1 Entire Agreement</h3>
              <p>These Terms, together with:</p>
              <ul>
                <li>Our Privacy Policy</li>
                <li>GLBA Confidentiality Agreement</li>
                <li>Any service-specific agreements or addendums</li>
              </ul>
              <p>constitute the entire agreement between you and Pacific Document Signing, Inc. regarding the Service, superseding any prior agreements or understandings.</p>

              <h3>15.2 Severability</h3>
              <p>If any provision of these Terms is found to be unenforceable, invalid, or prohibited by applicable law, that provision shall be limited or eliminated to the minimum extent necessary. The remaining provisions shall remain in full force and effect and shall be construed to give maximum effect to the parties' intent.</p>

              <h3>15.3 No Waiver</h3>
              <p>Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. Any waiver must be in writing and signed by an authorized representative of Pacific Document Signing, Inc. No waiver of any breach shall constitute a waiver of any subsequent breach.</p>

              <h3>15.4 Assignment and Transfer</h3>
              <ul>
                <li><strong>Your Rights</strong>: You may not assign, transfer, or delegate these Terms or your account without our prior written consent. Any unauthorized assignment is void.</li>
                <li><strong>Our Rights</strong>: We may assign our rights and obligations under these Terms without restriction, including to:
                  <ul>
                    <li>Affiliated companies or subsidiaries</li>
                    <li>Successors in interest or merger partners</li>
                    <li>Third-party service providers (with appropriate data protection)</li>
                  </ul>
                </li>
              </ul>

              <h3>15.5 Force Majeure</h3>
              <p>We shall not be liable for any delay or failure to perform resulting from causes outside our reasonable control, including:</p>
              <ul>
                <li>Acts of God (earthquakes, floods, fires, natural disasters)</li>
                <li>War, terrorism, riots, civil unrest, or government actions</li>
                <li>Labor disputes, strikes, or lockouts</li>
                <li>Epidemics, pandemics, or public health emergencies</li>
                <li>Internet or telecommunications failures</li>
                <li>Failures of third-party services (cloud providers, SMS/email services)</li>
                <li>Power outages or utility failures</li>
                <li>Cyberattacks or security incidents affecting infrastructure</li>
              </ul>

              <h3>15.6 Notices</h3>
              <p>All notices required under these Terms must be in writing and sent to:</p>
              <div className="contact-info">
                <p><strong>To PacDocSign:</strong></p>
                <p>Pacific Document Signing Inc.</p>
                <p>2154 Westwood Blvd. Suite #300</p>
                <p>Los Angeles, CA 90025</p>
                <p>Email: legal@pacdocsign.com</p>
              </div>
              <p><strong>To You:</strong> At the email address or physical address provided in your account information</p>
              <p>Notices are deemed received:</p>
              <ul>
                <li>If by email: 24 hours after sending</li>
                <li>If by mail: 3 business days after postmark</li>
                <li>If by courier: Upon delivery confirmation</li>
              </ul>

              <h3>15.7 Relationship of Parties</h3>
              <p>Nothing in these Terms creates a partnership, joint venture, employment, or agency relationship between:</p>
              <ul>
                <li>PacDocSign and signing agents (who are independent contractors)</li>
                <li>PacDocSign and clients</li>
                <li>Different users of the Service</li>
              </ul>
              <p>Each party is an independent contractor responsible for their own taxes, insurance, and legal obligations.</p>

              <h3>15.8 Third-Party Beneficiaries</h3>
              <p>These Terms are for the benefit of Pacific Document Signing, Inc. and you alone. No third party has any right to enforce any provision of these Terms.</p>

              <h3>15.9 Language</h3>
              <p>These Terms are written in English. Any translations are provided for convenience only. In case of conflict between English and translated versions, the English version controls.</p>

              <h3>15.10 Electronic Signatures</h3>
              <p>Your electronic acceptance of these Terms (by clicking "I Accept," creating an account, or using the Service) constitutes your electronic signature and binding agreement, equivalent to a handwritten signature.</p>
            </section>

            <section className="terms-section">
              <h2>16. Contact Information</h2>
              <p>For questions, concerns, or notices regarding these Terms of Service, please contact us:</p>
              <div className="contact-info">
                <p><strong>Pacific Document Signing Inc.</strong></p>
                <p>2154 Westwood Blvd. Suite #300</p>
                <p>Los Angeles, CA 90025</p>
                <br />
                <p><strong>Telephone:</strong> 800-884-7587</p>
                <p><strong>Fax:</strong> 800-732-4494</p>
                <p><strong>Email:</strong> support@pacdocsign.com</p>
                <p><strong>Legal Inquiries:</strong> legal@pacdocsign.com</p>
                <br />
                <p><strong>Business Hours:</strong></p>
                <p>Monday - Friday: 8:00 AM - 6:00 PM Pacific Time</p>
                <p>Saturday: 9:00 AM - 2:00 PM Pacific Time</p>
                <p>Sunday: Closed</p>
              </div>
            </section>

            <section className="terms-section acknowledgment">
              <h2>Acknowledgment and Acceptance</h2>
              <p><strong>BY CREATING AN ACCOUNT, ACCESSING, OR USING THE PACDOCSIGN SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE:</strong></p>
              <ul>
                <li>✓ Read these Terms of Service in their entirety</li>
                <li>✓ Understand all provisions, including limitations of liability and dispute resolution</li>
                <li>✓ Agree to be legally bound by these Terms</li>
                <li>✓ Consent to receive electronic communications</li>
                <li>✓ Waive any right to participate in class actions (where permitted by law)</li>
              </ul>
              <p><strong>If you do not agree to these Terms, you must immediately cease all use of the Service and may not create an account.</strong></p>
            </section>

            <section className="terms-section">
              <h2>Modification History</h2>
              <p><strong>Version 2.0 - October 16, 2025</strong></p>
              <ul>
                <li>Updated to reflect actual Service features and functionality</li>
                <li>Added detailed account type descriptions (Individual, Company, roles)</li>
                <li>Included specific SMS provider information and keywords</li>
                <li>Added QuickBooks integration details for payment processing</li>
                <li>Specified encryption methods (CSEK) for document security</li>
                <li>Enhanced GLBA compliance requirements</li>
                <li>Updated contact information</li>
                <li>Clarified communication service providers (Plivo/Twilio, SendGrid, Office365)</li>
              </ul>
              <p><strong>Version 1.0 - [Previous Date]</strong></p>
              <ul>
                <li>Initial Terms of Service</li>
              </ul>
            </section>

            <section className="terms-section">
              <p className="effective-date">
                <em>These Terms of Service are effective as of October 16, 2025, and will remain in effect except with respect to any changes in their provisions, which will be effective immediately after being posted on this page with appropriate notice to users.</em>
              </p>
              <p className="effective-date">
                <em>Last Review Date: October 16, 2025</em>
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default TermsAndConditions;